import { z } from 'zod';
import { RelatedEntity, Department } from '../enums';

export const FieldDefinitionSchema: z.ZodType<any> = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  type: z.enum([
    'text', 'textarea', 'number', 'boolean', 'date', 'time', 'datetime',
    'time_range', 'select', 'multiselect', 'address', 'phone', 'email',
    'url', 'currency', 'user_select', 'object', 'file',
  ]),
  required: z.boolean().optional(),
  default: z.any().optional(),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  options: z.array(z.string()).optional(),
  fields: z.lazy(() => z.array(FieldDefinitionSchema)).optional(),
  validation: z.object({
    min: z.number().optional(),
    max: z.number().optional(),
    pattern: z.string().optional(),
    message: z.string().optional(),
  }).optional(),
});

export const EventTypeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  color: z.string(),
  bg_color: z.string().nullable(),
  icon: z.string().nullable(),
  related_entity: z.nativeEnum(RelatedEntity).nullable(),
  field_schema: z.array(FieldDefinitionSchema),
  default_departments: z.array(z.nativeEnum(Department)),
  default_duration_minutes: z.number(),
  requires_time: z.boolean(),
  all_day_default: z.boolean(),
  allow_overlap: z.boolean(),
  requires_assignment: z.boolean(),
  is_task: z.boolean(),
  is_system: z.boolean(),
  is_active: z.boolean(),
  sort_order: z.number(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const CreateEventTypeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  slug: z.string().min(1).max(100).regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with hyphens'),
  description: z.string().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
  bg_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  icon: z.string().max(50).optional(),
  related_entity: z.nativeEnum(RelatedEntity).nullable().optional(),
  field_schema: z.array(FieldDefinitionSchema).default([]),
  default_departments: z.array(z.nativeEnum(Department)).default([]),
  default_duration_minutes: z.number().int().positive().default(60),
  requires_time: z.boolean().default(true),
  all_day_default: z.boolean().default(false),
  allow_overlap: z.boolean().default(true),
  requires_assignment: z.boolean().default(false),
});

export const UpdateEventTypeSchema = CreateEventTypeSchema.partial().omit({ slug: true }).extend({
  is_active: z.boolean().optional(),
});

export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;
export type EventType = z.infer<typeof EventTypeSchema>;
export type CreateEventType = z.infer<typeof CreateEventTypeSchema>;
export type UpdateEventType = z.infer<typeof UpdateEventTypeSchema>;
